import { Injectable } from "@angular/core";
import { Adapter } from "@core/adapter";
import { Place } from "../place.model";
import { SessionListItem } from "./session-list-item.model";

export type CourseOverviewType = 'branch' | 'training_plan';
export type CourseOverviewStatus = 'unresolved' | 'failed' | 'passed';

// The relevant session for the item (next upcoming for current, most recent for finished). Extends SessionListItem
// so it can be passed straight to app-sessionlistitem-datetime; endDate carries the far bound of a date range.
export interface CourseOverviewSession extends SessionListItem {
  endDate: Date | null;
}

export interface CourseOverviewItem {
  type: CourseOverviewType;
  detailId: string;          // branch -> series id (/courses/:id); training_plan -> trainingPlanCourse id (getCourseInfo)
  sessionId: string | null;  // branch live session -> "watch session"; null for training plan

  courseName: string;
  seriesName: string;
  isBundle: boolean;         // when true, the series name is the primary label and the course name is secondary
  cover: string;
  sessionTotal: number;
  status: CourseOverviewStatus | null;

  branchName: string;
  branchLogo: string;
  branchColor: string;
  branchUrl: string;
  organisationId: string;
  organisationName: string;
  organisationLogo: string;

  session: CourseOverviewSession | null;

  courseDateType: string;
  courseLocation: string;
  courseLocationName: string;
}

export interface CourseOverviewResult {
  items: CourseOverviewItem[];
  total: number;
  page: number;
  limit: number;
}

@Injectable({
  providedIn: 'root'
})
export class CourseOverviewItemAdapter implements Adapter<CourseOverviewItem> {
  adapt(item: any): CourseOverviewItem {
    return {
      type: item.type,
      detailId: item.detailId ?? '',
      sessionId: item.sessionId ?? null,
      courseName: (item.courseName ?? '').trim(),
      seriesName: (item.seriesName ?? '').trim(),
      isBundle: item.isBundle ?? false,
      cover: item.cover ?? '',
      sessionTotal: item.sessionTotal ?? 0,
      status: item.status ?? null,
      branchName: item.branchName ?? '',
      branchLogo: item.branchLogo ?? '',
      branchColor: item.branchColor ? item.branchColor : '#0b145a',
      branchUrl: item.branchUrl ?? '',
      organisationId: item.organisationId ?? '',
      organisationName: item.organisationName ?? '',
      organisationLogo: item.organisationLogo ?? '',
      session: item.session ? this.adaptSession(item.session) : null,
      courseDateType: item.courseDateType ?? '',
      courseLocation: item.courseLocation ?? '',
      courseLocationName: item.courseLocationName ?? ''
    };
  }

  private adaptSession(s: any): CourseOverviewSession {
    return {
      id: s.id ?? '',
      name: (s.name ?? '').trim(),
      date: s.date ? new Date(s.date) : undefined,
      endDate: s.endDate ? new Date(s.endDate) : null,
      startTime: s.startTime ?? '',
      endTime: s.endTime ?? '',
      location: s.location ? new Place(s.location.id ?? '', s.location.name ?? '', s.location.description ?? '', s.location.address) : undefined,
      onDemand: s.onDemand ?? false,
      inThePast: s.inThePast ?? false
    };
  }
}
